if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.select = this.querySelector('.sticky-atc__select');
        this.submitButton = this.querySelector('.sticky-atc__button');
        this.priceContainer = this.querySelector('.sticky-atc__price');
        this.observer = null;
        this.variantChangeUnsubscriber = undefined;
        this.variants = this.parseVariants();
      }

      connectedCallback() {
        this.removeAttribute('hidden');
        this.setupObserver();

        if (this.select) {
          this.select.addEventListener('change', this.onStickySelectChange.bind(this));
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onVariantChange.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.variantChangeUnsubscriber?.();
      }

      parseVariants() {
        const script = this.querySelector('[data-sticky-variants]');
        if (!script) return [];
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error('Sticky cart: failed to parse variants', error);
          return [];
        }
      }

      // Show the bar only when the main add-to-cart button is out of view.
      setupObserver() {
        const target = document.querySelector(this.dataset.submitSelector);
        if (!target || !('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const scrolledPast = entry.boundingClientRect.top < 0;
              if (!entry.isIntersecting && scrolledPast) {
                this.classList.add('is-visible');
              } else {
                this.classList.remove('is-visible');
              }
            });
          },
          { root: null, threshold: 0 }
        );

        this.observer.observe(target);
      }

      // Sticky select changed → update ONLY the sticky bar (price + button).
      // We deliberately do NOT drive the main variant picker here: doing so
      // triggers Dawn's gallery scroll (jumping the page to the top) and would
      // reveal the main add-to-cart, hiding the sticky bar. The sticky form
      // submits its own variant id, so the correct variant is always added.
      onStickySelectChange() {
        const variantId = this.select?.value;
        if (!variantId) return;

        const variant = this.variants.find((v) => String(v.id) === String(variantId));
        this.renderPrice(variant);
        this.updateButtonState(variant);
      }

      // Main variant picker changed → reflect into the sticky bar.
      onVariantChange(event) {
        const data = event?.data;
        if (!data || String(data.sectionId) !== String(this.sectionId)) return;

        const variant = data.variant;

        if (this.select && variant) {
          const optionToSelect = Array.from(this.select.options).find(
            (opt) => opt.value === String(variant.id)
          );
          if (optionToSelect) this.select.value = String(variant.id);
        }

        const mapped = variant ? this.variants.find((v) => String(v.id) === String(variant.id)) : null;
        this.renderPrice(mapped);
        this.updateButtonState(variant);
      }

      renderPrice(variant) {
        if (!this.priceContainer || !variant) return;

        if (variant.on_sale && variant.compare) {
          this.priceContainer.innerHTML =
            '<div class="price price--on-sale">' +
            '<div class="price__container">' +
            '<div class="price__regular"><span class="price-item price-item--regular">' +
            variant.compare +
            '</span></div>' +
            '<div class="price__sale"><span class="price-item price-item--sale price-item--last">' +
            variant.price +
            '</span></div>' +
            '</div></div>';
        } else {
          this.priceContainer.innerHTML =
            '<div class="price">' +
            '<div class="price__container">' +
            '<div class="price__regular"><span class="price-item price-item--regular price-item--last">' +
            variant.price +
            '</span></div>' +
            '</div></div>';
        }
      }

      updateButtonState(variant) {
        if (!this.submitButton) return;
        const buttonText = this.submitButton.querySelector('span');

        // variant can be the mapped object (has `available`) or the Dawn
        // variant object (also has `available`). Handle both / missing.
        const available = variant ? variant.available !== false : false;

        if (!variant) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (buttonText) buttonText.textContent = window.variantStrings?.unavailable || '';
        } else if (!available) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (buttonText) buttonText.textContent = window.variantStrings?.soldOut || '';
        } else {
          this.submitButton.removeAttribute('disabled');
          if (buttonText) buttonText.textContent = window.variantStrings?.addToCart || '';
        }
      }
    }
  );
}
